import { TimeZoneType } from "../../../constants/timezones"
import { Option } from "../../types"
import { TaskActivityRow } from "../types"
import { DateTimeService } from "../../../services/date-time-service"
import {
  getCustomerOptions,
  getMembershipOptions,
  getOptionId,
  getOptionName,
  getSupplierOptions,
  getWorkspaceOptions,
} from "../../../options/utils"
import {
  getColorHtmlForColor,
  getStateIconHtml,
  makeEnumKeysFromStatusOptions,
  makeEnumString,
  makeStatusEnumFromStatusOptions,
  mapColorToTranslations,
  mapStatusToTranslations,
} from "../../utils"
import { sortBy } from "lodash"
import { isTaskPlannedEndDatePastDue, isTaskPlannedStartDatePastDue } from "../../../tasks/utils/date"
import { TaskViewModel } from "../../../tasks/api/task"
import { TreeGridTranslations } from "../../hooks/use-translations"
import { getGanttGanttHtmlRight, getTitleHtmlPrefix } from "../../utils/tree-grid"
import { Status } from "../../../types/common"
import { getTitleFilterValue } from "../../base/filtering/title-filter-value"

type MakeTaskActivityRowProps = {
  task: TaskViewModel
  dateFormat: string
  timeZone: TimeZoneType
  options: Option[]
  translations: TreeGridTranslations
}
const getPlannedStartDate = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  return task.plannedStartDate ? dateTimeService.removeTimezoneOffset(task.plannedStartDate, "UTC").getTime() : ""
}

const getPlannedEndDate = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  return task.plannedEndDate ? dateTimeService.removeTimezoneOffset(task.plannedEndDate, "UTC").getTime() : ""
}

const getActualStartDate = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  return task.actualStartDate ? dateTimeService.removeTimezoneOffset(task.actualStartDate, "UTC").getTime() : ""
}

const getActualBarStartDate = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  return getActualStartDate(task, dateTimeService)
}

const getActualBarEndDate = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  const now = new Date().toISOString()
  return task.isInProgress ? dateTimeService.removeTimezoneOffset(now, "UTC").getTime() : ""
}

const getPlannedStartDateClass = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  const isPastDue = isTaskPlannedStartDatePastDue({ task, dateTimeService })
  return isPastDue ? "redText" : ""
}

const getPlannedEndDateClass = (task: TaskViewModel, dateTimeService: DateTimeService) => {
  const isPastDue = isTaskPlannedEndDatePastDue({ task, dateTimeService })
  return isPastDue ? "redText" : ""
}

const getStatusDescriptionTip = (task: TaskViewModel, dateTimeService: DateTimeService, dateFormat: string) => {
  const sortedUpdates = sortBy(task.statusDescriptionUpdates, "updatedAt")
  const latestUpdate = sortedUpdates[sortedUpdates.length - 1]
  let tip = ""
  if (latestUpdate) {
    const formatStr = `${dateFormat} HH:mm`
    const { updatedBy, updatedAt } = latestUpdate
    const offsetDate = dateTimeService.removeTimezoneOffset(updatedAt)
    tip += updatedBy
    tip += "&nbsp;&nbsp;&bull;&nbsp;&nbsp;"
    tip += dateTimeService.format(offsetDate, formatStr)
  }
  return tip
}

const makeFilterOptionByOrganisation = (task: TaskViewModel) => {
  return (option: Option) => option.orgId === task.maintainerId
}

export const makeTaskActivityRow = ({
  task,
  dateFormat,
  timeZone,
  options,
  translations,
}: MakeTaskActivityRowProps): TaskActivityRow => {
  const filterOptionByOrganisation = makeFilterOptionByOrganisation(task)
  const {
    id,
    title,
    description,
    statusDescription,
    enableTimeComponent,
    projectLink,
    ancestorTaskLinks,
    plannedEndDate,
    plannedStartDate,
  } = task
  const dateTimeService = new DateTimeService({ dateFormat, timeZone, enableTimeComponent })
  const dateTimeFormat = dateTimeService.getFormat()
  const statusTranslations = mapStatusToTranslations(translations)
  const status = statusTranslations[task.customStatus as Status] || task.customStatus
  const statusEnum = makeStatusEnumFromStatusOptions(task.statusOptions, translations)
  const statusEnumKeys = makeEnumKeysFromStatusOptions(task.statusOptions)
  const ganttGanttHtmlRight = getGanttGanttHtmlRight({
    title: title,
    startDate: plannedStartDate,
    endDate: plannedEndDate,
    dateTimeService,
    showTime: enableTimeComponent,
  })
  const GanttGanttHtmlRight = "*ganttGanttHtmlRight*"
  const translatedColor = mapColorToTranslations(translations)[task.ganttBarColor]
  const ganttBarColor = getColorHtmlForColor({ color: task.ganttBarColor, translatedColor })
  const GanttGanttClass = task.ganttBarColor === "Default" ? "Blue" : task.ganttBarColor

  const customers = task.customers.map((c) => c.id).join(";")
  const customerOptions = getCustomerOptions(options).filter(filterOptionByOrganisation)
  const customerNames = customerOptions.map(getOptionName)
  const customerIds = customerOptions.map(getOptionId)
  const deactivatedCustomers = task.customers.filter(({ id }) => !customerIds.includes(id))
  const deactivatedCustomersNames = deactivatedCustomers.map(({ name }) => name)
  const deactivatedCustomersIds = deactivatedCustomers.map(({ id }) => id)
  const customersEnum = makeEnumString([...customerNames, ...deactivatedCustomersNames])
  const customersEnumKeys = makeEnumString([...customerIds, ...deactivatedCustomersIds])

  const responsible = task.managers.map((r) => r.id).join(";")
  const membershipOptions = getMembershipOptions(options).filter(filterOptionByOrganisation)
  const responsibleNames = membershipOptions.map(getOptionName)
  const responsibleIds = membershipOptions.map(getOptionId)
  const deactivatedResponsible = task.managers.filter(({ id }) => !responsibleIds.includes(id))
  const deactivatedResponsibleNames = deactivatedResponsible.map(({ name }) => name)
  const deactivatedResponsibleIds = deactivatedResponsible.map(({ id }) => id)
  const responsibleEnum = makeEnumString([...responsibleNames, ...deactivatedResponsibleNames])
  const responsibleEnumKeys = makeEnumString([...responsibleIds, ...deactivatedResponsibleIds])

  const participants = task.participants.map((r) => r.id).join(";")
  const participantOptions = membershipOptions.filter(({ id }) => !responsible.includes(id))
  const participantNames = participantOptions.map(getOptionName)
  const participantIds = participantOptions.map(getOptionId)
  const deactivatedParticipants = task.participants.filter(({ id }) => !participantIds.includes(id))
  const deactivatedParticipantsNames = deactivatedParticipants.map(({ name }) => name)
  const deactivatedParticipantsIds = deactivatedParticipants.map(({ id }) => id)
  const participantsEnum = makeEnumString([...participantNames, ...deactivatedParticipantsNames])
  const participantsEnumKeys = makeEnumString([...participantIds, ...deactivatedParticipantsIds])

  const suppliers = task.suppliers.map((s) => s.id).join(";")
  const supplierOptions = getSupplierOptions(options).filter(filterOptionByOrganisation)
  const supplierNames = supplierOptions.map(getOptionName)
  const supplierIds = supplierOptions.map(getOptionId)
  const deactivatedSuppliers = task.suppliers.filter(({ id }) => !supplierIds.includes(id))
  const deactivatedSuppliersNames = deactivatedSuppliers.map(({ name }) => name)
  const deactivatedSuppliersIds = deactivatedSuppliers.map(({ id }) => id)
  const suppliersEnum = makeEnumString([...supplierNames, ...deactivatedSuppliersNames])
  const suppliersEnumKeys = makeEnumString([...supplierIds, ...deactivatedSuppliersIds])

  const workspaces = task.workspaces.map((w) => w.id).join(";")
  const workspaceOptions = getWorkspaceOptions(options).filter(filterOptionByOrganisation)
  const workspaceNames = workspaceOptions.map(getOptionName)
  const workspaceIds = workspaceOptions.map(getOptionId)
  const deactivatedWorkspaces = task.workspaces.filter(({ id }) => !workspaceIds.includes(id))
  const deactivatedWorkspacesNames = deactivatedWorkspaces.map(({ name }) => name)
  const deactivatedWorkspacesIds = deactivatedWorkspaces.map(({ id }) => id)
  const workspacesEnum = makeEnumString([...workspaceNames, ...deactivatedWorkspacesNames])
  const workspacesEnumKeys = makeEnumString([...workspaceIds, ...deactivatedWorkspacesIds])

  const linksHtml = task.links.map((link) => link.anchorTag)
  let filesHtml: string[] = []
  if (task.canViewTaskAttachments && task.files.length > 0) {
    filesHtml = task.files.map(
      (file) => `<a href="#" data-file-id="${file.id}" onclick="return false;">${file.filename}</a>`
    )
  }
  const attachmentsHtml = [...linksHtml, ...filesHtml].join(", ")

  return {
    id,
    type: "task",
    projectId: task.projectId,
    open: "/external-link-icon.svg",
    activityNumber: task.customTaskNumber || task.taskNumber,
    title,
    titleHtmlPrefix: getTitleHtmlPrefix({ projectLink, ancestorTaskLinks, taskLink: null }),
    description,
    status,
    state: getStateIconHtml(task.state),
    statusDescription,
    statusDescriptionTip: getStatusDescriptionTip(task, dateTimeService, dateFormat),
    enableTimeComponent,
    plannedStartDate: getPlannedStartDate(task, dateTimeService),
    plannedStartDateClass: getPlannedStartDateClass(task, dateTimeService),
    plannedStartDateFormat: dateTimeFormat,
    plannedEndDate: getPlannedEndDate(task, dateTimeService),
    plannedEndDateClass: getPlannedEndDateClass(task, dateTimeService),
    plannedEndDateFormat: dateTimeFormat,
    actualStartDate: getActualStartDate(task, dateTimeService),
    actualStartDateFormat: dateTimeFormat,
    actualBarStartDate: getActualBarStartDate(task, dateTimeService),
    actualBarEndDate: getActualBarEndDate(task, dateTimeService),
    GanttGanttHtmlRight,
    ganttGanttHtmlRight,
    GanttGanttClass,
    ganttBarColor,
    ganttBarColorFilterValue: translatedColor,
    titleFilterValue: getTitleFilterValue(task),
    responsible,
    responsibleEnum,
    responsibleEnumKeys,
    participants,
    participantsEnum,
    participantsEnumKeys,
    customers,
    customersEnum,
    customersEnumKeys,
    suppliers,
    suppliersEnum,
    suppliersEnumKeys,
    workspaces,
    workspacesEnum,
    workspacesEnumKeys,
    linksAndAttachments: attachmentsHtml,
    completionPercentage: task.completionPercentage,
    completionPercentageHtmlPostfix: "%",
    completionPercentageBasedOnTasks: "",
    completionPercentageBasedOnTasksHtmlPostfix: "",
    activityNumberCanEdit: task.canUpdateDetails ? 1 : 0,
    titleCanEdit: task.canUpdateDetails ? 1 : 0,
    descriptionCanEdit: task.canUpdateDetails ? 1 : 0,
    ganttBarColorCanEdit: task.canUpdateDetails ? 1 : 0,
    plannedStartDateCanEdit: task.canUpdatePlan ? 1 : 0,
    plannedEndDateCanEdit: task.canUpdatePlan ? 1 : 0,
    statusCanEdit: task.canUpdateStatus ? 1 : 0,
    stateCanEdit: task.canUpdateStatus ? 1 : 0,
    statusDescriptionCanEdit: task.canUpdateStatus ? 1 : 0,
    actualStartDateCanEdit: task.canUpdateStatus ? 1 : 0,
    customersCanEdit: 0,
    responsibleCanEdit: task.canUpdateManagers && task.isOrgTask ? 1 : 0,
    participantsCanEdit: task.canUpdateParticipants && task.isOrgTask ? 1 : 0,
    suppliersCanEdit: task.canUpdateSuppliers && task.isOrgTask ? 1 : 0,
    workspacesCanEdit: task.canUpdateWorkspaces && task.isOrgTask ? 1 : 0,
    completionPercentageCanEdit: task.canUpdateStatus ? 1 : 0,
    completionPercentageBasedOnTasksCanEdit: 0,
    Height: 48,
    MaxHeight: 48,
    statusEnum,
    statusEnumKeys,
  }
}
